import styled from 'styled-components';

import { EditNew16, InfoLine16, View16 } from 'ui/icons';
import { LargeSegmentedControl, SegmentedControlOption } from 'ui/segmentedControl';
import { iconColor } from 'ui/segmentedControl/utils';
import { spacing } from 'ui/theme';
import {
  InAppContentDefinition,
  MessageMedium,
  MESSAGE_FORMAT,
  HtmlEditor,
} from 'modules/graphql/types';

import { useTemplateQueryContext } from 'modules/templateEditor/context/TemplateQueryContext';
import { TemplateEditViews } from 'modules/templateEditor/common/types';
import { useEditableFieldsContext } from 'modules/templateEditor/context/EditableFieldsContext';
import { DISABLED_DESIGN_BUTTON_TEXT } from 'modules/templateEditor/common/constants';
import { LostProgressModal } from 'modules/templateEditor/modals/LostProgressModal';
import { useCallback, useState } from 'react';
import { useSaveTemplate } from 'modules/templateEditor/hooks/useSaveTemplate';
import { useInStudioTakeoverContext } from '../../context';

const unsavedModalContent = {
  header: 'Unsaved changes',
  body: 'This template has been edited. Do you want to save your changes?',
  cancel: 'Cancel',
  leave: 'Continue without saving',
  save: 'Save and continue',
};

const SwitchOption = styled.div`
  align-items: center;
  column-gap: ${spacing.half};
  display: flex;
  justify-content: center;
`;

type GetOptionsParams = {
  isDisabled: boolean;
  isEmailOrInApp: boolean;
  options: Option[];
  selectedView: string;
};

type Option = {
  Icon: React.ElementType;
  isDisabled?: boolean;
  isSelected?: boolean;
  label: string;
  tooltip?: Tooltip;
  view: TemplateEditViews;
};

type Tooltip = {
  content: string;
};

const option = ({ isDisabled, view, isSelected = false, Icon, label }: Option) => {
  return {
    disabled: isDisabled,
    key: `option-view-${view}`,
    name: (
      <SwitchOption data-test={`option-view-${view}`}>
        <Icon color={iconColor(isSelected, isDisabled)} />
        <span>{label}</span>
      </SwitchOption>
    ),
    value: view,
  };
};

const getOptions = ({ isDisabled, isEmailOrInApp, options, selectedView }: GetOptionsParams) => {
  return options.reduce<SegmentedControlOption[]>((acc, opt) => {
    const isSelected = selectedView === opt.view;

    if (opt.view === TemplateEditViews.content && !isEmailOrInApp) return acc;
    return [...acc, option({ ...opt, isDisabled, isSelected })];
  }, []);
};

export const ViewSwitchToggle = () => {
  const { medium, refetch } = useTemplateQueryContext();
  const { setView, view } = useInStudioTakeoverContext();
  const { saveTemplate } = useSaveTemplate();
  const { liveTemplate, isDirty, contentEditor, setDragAndDropInitTs, setDragAndDropInitValue } =
    useEditableFieldsContext();
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [transition, setTransition] = useState<string | undefined>();
  const isDisabled = false;
  const isEmailOrInApp = medium === MessageMedium.Email || medium === MessageMedium.InApp;
  const { mediumData = {} } = liveTemplate || {};
  const { messageFormat } = mediumData as InAppContentDefinition;
  const isJsonOnly = messageFormat === MESSAGE_FORMAT.Json;

  const handleSelect = useCallback(
    (nextView: string) => {
      if (isDirty) {
        setModalVisible(true);
        setTransition(nextView);
      } else {
        setView(nextView);
      }
    },
    [isDirty],
  );

  const onClose = () => {
    setModalVisible(false);
  };

  const onLeave = () => {
    refetch(); // revert changes
    if (contentEditor === HtmlEditor.Bee) {
      setDragAndDropInitValue();
      setDragAndDropInitTs();
    }
    if (transition) setView(transition);
    setModalVisible(false);
    setTransition(undefined);
  };

  const onSave = () => {
    saveTemplate(() => {
      refetch();
      if (transition) setView(transition);
      setModalVisible(false);
      setTransition(undefined);
    });
  };

  return (
    <>
      {isDirty && modalVisible && (
        <LostProgressModal
          onClose={onClose}
          onLeave={onLeave}
          onSave={onSave}
          isModalOpen={modalVisible}
          modalContent={unsavedModalContent}
        />
      )}

      <LargeSegmentedControl
        onSelect={handleSelect}
        options={getOptions({
          options: [
            {
              view: TemplateEditViews.settings,
              Icon: InfoLine16,
              label: 'Details',
            },
            {
              view: TemplateEditViews.content,
              Icon: EditNew16,
              label: 'Design',
              isDisabled: isJsonOnly,
              tooltip: {
                content: DISABLED_DESIGN_BUTTON_TEXT,
              },
            },
            {
              view: TemplateEditViews.preview,
              Icon: View16,
              label: 'Preview',
            },
          ],
          isDisabled,
          selectedView: view as TemplateEditViews,
          isEmailOrInApp,
        })}
        value={view}
      />
    </>
  );
};

export default ViewSwitchToggle;
